import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { Shield, ArrowLeft } from '@phosphor-icons/react';

export default function Safeguarding() {
  return (
    <div className="min-h-screen py-12">
      <SEO
        title="Safeguarding Policy — Keeping Students Safe Online"
        description="Grade9Ready's safeguarding policy. Learn how we protect children and young people with secure pairing codes, no public profiles, and age-appropriate content. Designed for KS3 and KS4 students."
        path="/safeguarding"
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-8">
          <ArrowLeft size={16} /> Back to Home
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-emerald-500 rounded-xl border-2 border-foreground flex items-center justify-center shadow-[3px_3px_0px_0px_rgb(var(--9ready-shadow))]">
            <Shield size={24} weight="fill" className="text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-heading font-bold">Safeguarding Policy</h1>
            <p className="text-muted-foreground text-sm">Grade9Ready Safeguarding Policy</p>
          </div>
        </div>

        <div className="prose prose-sm max-w-none space-y-8" data-testid="safeguarding-content">
          <section className="neo-card p-6">
            <h2 className="text-xl font-bold mb-3">1. Our Commitment</h2>
            <p className="text-muted-foreground leading-relaxed">
              Grade9Ready is committed to safeguarding and promoting the welfare of children and young people. We aim to provide a safe, secure, and supportive online learning environment.
            </p>
          </section>

          <section className="neo-card p-6">
            <h2 className="text-xl font-bold mb-3">2. Scope</h2>
            <p className="text-muted-foreground leading-relaxed">
              This policy applies to all users of the platform, particularly students under the age of 18 and their parents or guardians.
            </p>
          </section>

          <section className="neo-card p-6">
            <h2 className="text-xl font-bold mb-3">3. Key Safeguarding Risks</h2>
            <p className="text-muted-foreground mb-3">We recognise the following potential risks within an online platform:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>Unauthorised access to student accounts</li>
              <li>Exposure to inappropriate content</li>
              <li>Misuse of personal data</li>
              <li>Impersonation of parents or guardians</li>
            </ul>
          </section>

          <section className="neo-card p-6">
            <h2 className="text-xl font-bold mb-3">4. Safeguarding Measures</h2>

            <h3 className="text-lg font-bold mt-4 mb-2">4.1 Secure Parent Access (Pairing Code System)</h3>
            <p className="text-muted-foreground mb-3">To protect student accounts, Grade9Ready uses a Pairing Code System:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground mb-4">
              <li>Students generate a unique 6-character code</li>
              <li>Codes expire after 24 hours</li>
              <li>Parents must enter the student's email address and the valid pairing code</li>
            </ul>
            <p className="text-muted-foreground">
              This ensures that only a parent with direct access to the child can link accounts, reducing the risk of unauthorised access.
            </p>

            <h3 className="text-lg font-bold mt-6 mb-2">4.2 Platform Design</h3>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>No direct communication between users</li>
              <li>All content is designed to be age-appropriate</li>
              <li>No public user-generated content</li>
            </ul>

            <h3 className="text-lg font-bold mt-6 mb-2">4.3 Account Security</h3>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>Secure login required for all users</li>
              <li>Monitoring of suspicious activity</li>
              <li>Ability to reset or revoke access if needed</li>
            </ul>
          </section>

          <section className="neo-card p-6">
            <h2 className="text-xl font-bold mb-3">5. Responsibilities</h2>

            <h3 className="text-lg font-bold mt-2 mb-2">Parents/Guardians</h3>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground mb-4">
              <li>Must keep login details secure</li>
              <li>Should only link accounts using codes provided by their child</li>
            </ul>

            <h3 className="text-lg font-bold mb-2">Students</h3>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>Should not share pairing codes publicly</li>
              <li>Should report anything that feels unsafe</li>
            </ul>
          </section>

          <section className="neo-card p-6">
            <h2 className="text-xl font-bold mb-3">6. Reporting Concerns</h2>
            <p className="text-muted-foreground mb-3">Safeguarding concerns can be reported via:</p>
            <p className="font-bold">Email: <a href="mailto:safeguarding@grade9ready.com" className="text-primary hover:underline">safeguarding@grade9ready.com</a></p>
            <p className="text-muted-foreground mt-2">All concerns will be reviewed and acted upon promptly.</p>
          </section>

          <section className="neo-card p-6">
            <h2 className="text-xl font-bold mb-3">7. Review</h2>
            <p className="text-muted-foreground">This policy is reviewed regularly to ensure continued effectiveness.</p>
          </section>
        </div>
      </div>
    </div>
  );
}
